import React, { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';

/**
 * PollenDotsMap — renders a clean CartoDB base map with markers ONLY for pins
 * that have `verified: true` (i.e., coords set explicitly from Google Maps).
 * Auto-geocoded or unverified pins are deliberately hidden until an admin
 * pastes the exact URL in the Locations admin screen.
 *
 * Uses leaflet.markercluster so world-zoom views collapse dense pin clouds
 * into readable summary bubbles; zooming in breaks clusters apart into
 * individual colour-coded dots.
 */

// All roles render as circles — clear, legible on mobile, no shape noise.
// Colours picked for high contrast (none share a neighbouring hue).
const ROLE_STYLE = {
  place:    { color: '#0d9488', shape: 'circle' }, // teal
  hotel:    { color: '#2563eb', shape: 'circle' }, // blue
  guide:    { color: '#e11d48', shape: 'circle' }, // rose-red
  driver:   { color: '#eab308', shape: 'circle' }, // gold
  activity: { color: '#9333ea', shape: 'circle' }, // purple — distinct from place/hotel
};

// Enroute stops render as TEARDROP PINS (not dots) — they are traces of where
// the customer passed through, not dots of connection. Each subtype gets a
// distinct colour + glyph so the map immediately reads as "POI vs Lunch vs
// Fuel" without needing to click each pin.
const STOP_STYLE = {
  POI:          { color: '#7c3aed', glyph: '\u2605' },           // violet · star
  Breakfast:    { color: '#f59e0b', glyph: '\u2615' },           // amber  · hot beverage
  'Tea Break':  { color: '#ea580c', glyph: '\u{1F375}' },        // orange · teacup
  Lunch:        { color: '#059669', glyph: '\u{1F37D}' },        // emerald · plate w cutlery
  Snacks:       { color: '#e11d48', glyph: '\u{1F36A}' },        // rose · cookie
  Fuel:         { color: '#475569', glyph: '\u26FD' },           // slate · fuel pump
  Restroom:     { color: '#0284c7' /* sky-600 */, glyph: '\u{1F6BB}' }, // sky · restroom sign
  Other:        { color: '#6b7280', glyph: '\u{1F4CD}' },        // gray · round pushpin
};

const labelFor = (t) => ({ place: 'Place', hotel: 'Hotel', guide: 'Guide base', driver: 'Driver base', stop: 'Enroute stop', activity: 'Activity' }[t] || 'Place');

const PollenDotsMap = React.forwardRef(({ pins, height, aspect, onPinClick, homeBounds }, ref) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const clusterRef = useRef(null);
  const markersRef = useRef([]);
  const [ready, setReady] = useState(false);
  const [L, setL] = useState(null);

  React.useImperativeHandle(ref, () => containerRef.current);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Load leaflet FIRST and expose it globally — leaflet.markercluster
      // attaches itself to `window.L` at import-time, so the order matters.
      const mod = await import('leaflet');
      const Ll = mod.default || mod;
      if (typeof window !== 'undefined') window.L = Ll;
      await import('leaflet.markercluster');
      if (cancelled) return;
      delete Ll.Icon.Default.prototype._getIconUrl;
      Ll.Icon.Default.mergeOptions({
        iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
        iconUrl:       'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
        shadowUrl:     'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
      });
      setL(Ll);
      setReady(true);
    })();
    return () => { cancelled = true; };
  }, []);

  // Init map once.
  useEffect(() => {
    if (!ready || !containerRef.current || mapRef.current) return;
    if (containerRef.current._leaflet_id) return;
    // Default view — centred on India so the map is never empty even when no
    // verified pins exist yet.
    const m = L.map(containerRef.current, {
      scrollWheelZoom: false, zoomControl: true, attributionControl: true,
      zoomSnap: 0.25, zoomDelta: 0.5,
    }).setView([22, 82], 4);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      subdomains: 'abc', maxZoom: 19, crossOrigin: true,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(m);
    mapRef.current = m;
    return () => {
      markersRef.current.forEach(mk => { try { mk.remove(); } catch (e) { /* ignore */ } });
      markersRef.current = [];
      m.off(); m.remove(); mapRef.current = null;
    };
  }, [ready, L]);

  // Sync markers — every pin with valid coords shows on the map. Verified pins
  // render with a solid ring; auto-geocoded / inherited pins render with a softer,
  // dashed ring so admins can tell them apart at a glance. Pins that would stack
  // on identical coordinates are nudged in a ring so each one remains clickable.
  // Dense clouds are collapsed into cluster bubbles; zooming in breaks them apart.
  useEffect(() => {
    const m = mapRef.current;
    if (!m || !L) return;
    if (clusterRef.current) {
      try { m.removeLayer(clusterRef.current); } catch (e) { /* ignore */ }
      clusterRef.current = null;
    }
    markersRef.current.forEach(mk => { try { mk.remove(); } catch (e) { /* ignore */ } });
    markersRef.current = [];

    const valid = (pins || []).filter(p => p.lat != null && p.lng != null);
    if (!valid.length) {
      m.setView([22, 82], 4);
      return;
    }

    // Stagger overlapping pins in a tight ring so they don't hide each other
    // once the cluster splits them apart at higher zoom.
    const buckets = {};
    valid.forEach(p => {
      const k = `${Number(p.lat).toFixed(4)}|${Number(p.lng).toFixed(4)}`;
      (buckets[k] = buckets[k] || []).push(p);
    });
    const OFFSET = 0.012; // ~1.3km at the equator — small enough to look tight, big enough to not overlap.
    Object.values(buckets).forEach(group => {
      if (group.length === 1) {
        group[0]._plotLat = Number(group[0].lat);
        group[0]._plotLng = Number(group[0].lng);
        return;
      }
      group.forEach((p, i) => {
        const angle = (2 * Math.PI * i) / group.length;
        p._plotLat = Number(p.lat) + Math.sin(angle) * OFFSET;
        p._plotLng = Number(p.lng) + Math.cos(angle) * OFFSET;
      });
    });

    const maxTrips = Math.max(1, ...valid.map(p => p.trip_count || 1));
    const INIT_COL = { 'smiles': '#f59e0b', 'cultural-exchange': '#c2410c', 'sustainability': '#84cc16' };
    const INIT_LBL = { 'smiles': 'Smiles', 'cultural-exchange': 'Cultural Exchange', 'sustainability': 'Sustainability' };
    // Composes the "✨ A Pollen Dot of Care" badge for the popup. Includes
    // tiny coloured dots — one per tagged initiative — so the *kind* of
    // care is visible directly on the map without an extra tap. When the
    // pin carries `force_care` (e.g. every guide is a dot of care by
    // default), the badge still renders but only the explicit initiatives
    // get coloured dots; a guide with no vendor-level tags reads simply
    // "✨ A Pollen Dot of Care".
    const careBadgeHtml = (inits, fontSize = 11, force = false) => {
      const list = (Array.isArray(inits) ? inits : []).filter(k => INIT_COL[k]);
      if (!list.length && !force) return '';
      const dots = list.map(k => `<span title="${INIT_LBL[k]}" style="display:inline-block;width:6px;height:6px;border-radius:9999px;background:${INIT_COL[k]}"></span>`).join('');
      const dotsBlock = dots ? `<span style="display:inline-flex;gap:3px;align-items:center;margin-left:2px">${dots}</span>` : '';
      return `<div style="margin-top:6px;padding:4px 8px;border-radius:6px;background:linear-gradient(90deg,#fff7ed,#fef3c7);border:1px solid #fde68a;font-size:${fontSize}px;color:#92400e;font-weight:500;display:inline-flex;align-items:center;gap:5px">
        <span>✨</span><span>A Pollen Dot of Care</span>${dotsBlock}
      </div>`;
    };
    const makeDot = (style, tripCount, verified, inits, force) => {
      const trips = Math.max(1, tripCount || 1);
      const scale = 1 + 0.35 * ((trips - 1) / Math.max(1, maxTrips - 1));
      // Smaller core (10px base, was 12) — operators reported pins were
      // hiding the dots underneath. Halo (when present) sits behind.
      const core = Math.round(10 * scale);
      const hasInits = Array.isArray(inits) && inits.length > 0;
      // Halo also shows when force=true (e.g. every guide is a dot of
      // care by default) so a long-engagement guide pin glows even
      // without explicit vendor tags.
      const showHalo = hasInits || !!force;
      // Halo box is 1.6x the core so the pulse never crowds neighbours.
      const haloBox = showHalo ? Math.round(core * 1.6) : core;
      const box  = Math.max(haloBox, core) + 6;
      const anchor = Math.round(box / 2);
      const haloColor = hasInits ? (INIT_COL[inits[0]] || '#94a3b8') : '#f59e0b';
      const haloLayer = showHalo
        ? `<div class="pollen-dot-halo" style="background:${haloColor};width:${haloBox}px;height:${haloBox}px"></div>`
        : '';
      return L.divIcon({
        className: `pollen-dot pollen-dot-${style.shape}${verified ? '' : ' pollen-dot-inherited'}${showHalo ? ' pollen-dot-care' : ''}`,
        html: `${haloLayer}<div class="pollen-dot-core" style="background:${style.color};width:${core}px;height:${core}px"></div>`,
        iconSize: [box, box], iconAnchor: [anchor, anchor], popupAnchor: [0, -anchor + 2],
      });
    };

    // Enroute-stop teardrop pin. Looks visually distinct from the
    // connection dots (taller, anchored at the tip, glyph inside the head)
    // so a viewer can tell at a glance: dots = lasting connection, pins =
    // passed-through stop.
    const makeStopPin = (style, verified, inits) => {
      // Smaller teardrop — operators flagged that the stop pins were too
      // tall and visually buried the dots-of-connection underneath. New
      // size 22×28 keeps the same proportions but reads ~25% smaller.
      const W = 22, H = 28;
      const stroke = verified ? '#ffffff' : 'rgba(255,255,255,0.55)';
      const opacity = verified ? '1' : '0.85';
      const hasInits = Array.isArray(inits) && inits.length > 0;
      // Halo sits behind the pin's circular head (top half of teardrop).
      // We anchor it on the head — not the bottom tip — so the pulse
      // visually orbits the stop's icon, not the ground.
      const haloD = hasInits ? 26 : 0;
      const boxW = Math.max(W, haloD);
      const boxH = H + (hasInits ? Math.max(0, (haloD - W) / 2) : 0);
      const haloLayer = hasInits
        ? `<div class="pollen-dot-halo" style="background:${INIT_COL[inits[0]] || '#94a3b8'};width:${haloD}px;height:${haloD}px;top:14px;left:50%"></div>`
        : '';
      return L.divIcon({
        className: 'pollen-stop-pin' + (verified ? '' : ' pollen-stop-pin-inherited') + (hasInits ? ' pollen-stop-pin-care' : ''),
        html: `<div style="position:relative;width:${boxW}px;height:${boxH}px">
          ${haloLayer}
          <svg width="${W}" height="${H}" viewBox="0 0 28 36" xmlns="http://www.w3.org/2000/svg" style="position:absolute;left:50%;top:0;transform:translateX(-50%);opacity:${opacity};filter:drop-shadow(0 1px 2px rgba(0,0,0,.25))">
            <path d="M14 0 C 6.27 0 0 6.27 0 14 C 0 23 14 36 14 36 C 14 36 28 23 28 14 C 28 6.27 21.73 0 14 0 Z" fill="${style.color}" stroke="${stroke}" stroke-width="1.5"/>
            <circle cx="14" cy="14" r="9" fill="#ffffff"/>
            <text x="14" y="18.5" font-size="13" text-anchor="middle" font-family="-apple-system, system-ui, sans-serif">${style.glyph}</text>
          </svg>
        </div>`,
        iconSize: [boxW, boxH],
        iconAnchor: [boxW / 2, H],   // tip of teardrop on the coord
        popupAnchor: [0, -H + 6],
      });
    };

    // Cluster icon — small pill with the total count, styled to match the
    // Pollen Dots palette so the atlas reads cleanly at world zoom.
    const clusterIcon = (cluster) => {
      const count = cluster.getChildCount();
      // Inspect children to pick a dominant colour (most-frequent role).
      const counts = { place: 0, hotel: 0, guide: 0, driver: 0 };
      cluster.getAllChildMarkers().forEach(mk => {
        const t = mk.options?.pollenRole || 'place';
        counts[t] = (counts[t] || 0) + 1;
      });
      const dominant = Object.keys(counts).reduce((a, b) => counts[a] >= counts[b] ? a : b);
      const colour = (ROLE_STYLE[dominant] || ROLE_STYLE.place).color;
      const size = count < 10 ? 34 : count < 50 ? 40 : 48;
      return L.divIcon({
        className: 'pollen-cluster',
        html: `<div class="pollen-cluster-core" style="background:${colour};width:${size}px;height:${size}px;line-height:${size}px">${count}</div>`,
        iconSize: [size, size],
      });
    };

    const group = L.markerClusterGroup({
      maxClusterRadius: 45,
      showCoverageOnHover: false,
      spiderfyOnMaxZoom: true,
      iconCreateFunction: clusterIcon,
      chunkedLoading: true,
      // Past zoom 7 every pin renders individually so colour-coded roles
      // (guide/driver/hotel/place) are always visible at city scale.
      disableClusteringAtZoom: 7,
    });

    const added = [];
    valid.forEach(p => {
      const isStop = p.type === 'stop';
      const isHotel = p.type === 'hotel';
      const isActivity = p.type === 'activity';
      let icon, pollenRole;
      if (isStop) {
        const style = STOP_STYLE[p.subtype] || STOP_STYLE.Other;
        icon = makeStopPin(style, p.verified, p.initiatives);
        pollenRole = 'stop';
      } else {
        const style = ROLE_STYLE[p.type] || ROLE_STYLE.place;
        // Halo only when an initiative is explicitly tagged — having a
        // note is no longer enough to claim "Pollen Dot of Care".
        icon = makeDot(style, p.trip_count, p.verified, p.initiatives, p.force_care);
        pollenRole = p.type || 'place';
      }
      const marker = L.marker([p._plotLat, p._plotLng], { icon, pollenRole });

      // ---- Popup composition ----
      // Hotels & enroute stops get a richer card: name, role chip(s),
      // address line, connection count, and a tappable "Open in Google
      // Maps" link.
      //
      // For stops, the eyebrow lists EVERY unique subtype the place has
      // served this traveller across all their tours/days — e.g. a
      // restaurant the customer used for Breakfast on one trip and
      // Lunch on another shows "Breakfast · Lunch" so the popup
      // honestly reflects how the place was used, instead of locking
      // to whichever subtype loaded first.
      const escHtml = (s) => String(s || '')
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
      // Multi-paragraph notes: preserve line breaks the operator typed. We
      // escape first (XSS-safe) then convert any run of newlines into a
      // paragraph break, single newlines into a soft <br>. Empty lines
      // between sentences become visible spacing — the way the operator
      // wrote it.
      const escMultiline = (s) => escHtml(s)
        .replace(/\r\n?/g, '\n')
        .replace(/\n{2,}/g, '<br><br>')
        .replace(/\n/g, '<br>');
      const safeUrl = (u) => {
        const s = String(u || '').trim();
        if (!s) return '';
        return /^https?:\/\//i.test(s) ? s : '';
      };
      const stopRoles = isStop
        ? (Array.isArray(p.subtypes) && p.subtypes.length ? p.subtypes : [p.subtype || 'Other'])
        : [];
      const labelLine = isStop
        ? `<div style="font-size:10px;text-transform:uppercase;letter-spacing:0.05em;color:#64748b">${stopRoles.map(escHtml).join(' &middot; ')}</div>`
        : `<div style="font-size:10px;text-transform:uppercase;letter-spacing:0.05em;color:#64748b">${escHtml(labelFor(p.type))}</div>`;
      const tripLine = isStop
        ? '<div style="color:#64748b;font-size:10px">Enroute stop</div>'
        : `<div style="color:#64748b">${p.trip_count || 1} connection${(p.trip_count || 1) > 1 ? 's' : ''}</div>`;
      const pinNote = p.verified ? '' : '<div style="color:#94a3b8;font-size:10px;margin-top:2px">Approximate · pin exact URL to fix</div>';

      let bodyHtml;
      if (isHotel || isStop || isActivity) {
        // Address line: hotels → location (city); stops → notes if any;
        // activities → location (when present, e.g. "Mawlynnong").
        const addrRaw = isHotel ? (p.location || '')
                       : isActivity ? (p.location || '')
                       : (p.notes || '');
        const addrHtml = addrRaw
          ? `<div style="color:#475569;font-size:11px;display:flex;align-items:flex-start;gap:4px;margin-top:2px">
               <span style="color:#94a3b8;flex-shrink:0">📍</span>
               <span>${isStop ? escMultiline(addrRaw) : escHtml(addrRaw)}</span>
             </div>`
          : '';
        // Hotels and activities can carry their own per-tour notes — a
        // richer narrative shown as a quiet amber paragraph (e.g.
        // "Skiltang Heritage offered a warm and personal stay…").
        // The "✨ A Pollen Dot of Care" eyebrow is added ONLY when the
        // operator has explicitly tagged at least one initiative
        // (Smiles / Cultural Exchange / Sustainability). Without tags
        // the note still renders, but as a plain story — not a claim.
        const hasInitiatives = Array.isArray(p.initiatives) && p.initiatives.length > 0;
        const richNoteHtml = ((isHotel || isActivity) && (p.notes || '').trim())
          ? `<div style="margin-top:6px;padding:6px 10px;border-radius:8px;background:linear-gradient(135deg,#fff7ed,#fef3c7);border:1px solid #fde68a;color:#7c2d12;font-size:11px;line-height:1.5">
               ${hasInitiatives ? '<div style="font-size:9px;text-transform:uppercase;letter-spacing:0.06em;color:#92400e;font-weight:600;margin-bottom:3px">✨ A Pollen Dot of Care</div>' : ''}
               <div>${escMultiline(p.notes)}</div>
             </div>`
          : '';
        // Connection count — every visit/mention is one connection.
        const conn = Math.max(1, p.trip_count || 1);
        const connHtml = `
          <div style="color:#0d9488;font-size:11px;font-weight:500;margin-top:4px;display:flex;align-items:center;gap:4px">
            <span style="display:inline-block;width:6px;height:6px;border-radius:9999px;background:#0d9488"></span>
            ${conn} connection${conn === 1 ? '' : 's'}
          </div>`;
        // Hotels honour the welcome — "N nights at {hotel}" — so the
        // share map reads as a quiet little story, not a generic count.
        // Stops don't get this line (they're roadside, not lodging).
        const nightsHtml = (isHotel && p.nights)
          ? `<div style="color:#475569;font-size:11px;margin-top:2px;display:flex;align-items:center;gap:4px">
               <span style="display:inline-block;width:6px;height:6px;border-radius:9999px;background:#0ea5e9"></span>
               <span>${p.nights} night${p.nights === 1 ? '' : 's'} hosted you</span>
             </div>`
          : '';
        // Show the colourful Pollen Dot of Care initiative badge ONLY
        // when initiatives are explicitly tagged on this pin. If we're
        // already rendering them inside the rich amber narrative (with
        // its own "✨ A Pollen Dot of Care" eyebrow), skip this one to
        // avoid doubling up.
        const careHtml = (richNoteHtml && hasInitiatives)
          ? ''
          : careBadgeHtml(p.initiatives, 11);
        const url = safeUrl(p.google_maps_url) || `https://www.google.com/maps/?q=${p.lat},${p.lng}`;
        const openHtml = `
          <a href="${url}" target="_blank" rel="noopener noreferrer"
             style="display:inline-flex;align-items:center;gap:4px;margin-top:6px;padding:4px 10px;
                    background:#0d9488;color:#fff;font-size:11px;font-weight:500;
                    border-radius:9999px;text-decoration:none">
            Open in Maps →
          </a>`;
        bodyHtml = `<div style="font-size:12px;line-height:1.4;min-width:160px;max-width:240px">
          ${labelLine}
          <div style="font-weight:600;font-size:13px;margin-top:1px;color:#0f172a">${escHtml(p.name || '')}</div>
          ${addrHtml}
          ${connHtml}
          ${nightsHtml}
          ${richNoteHtml}
          ${careHtml}
          ${pinNote}
          ${openHtml}
        </div>`;
      } else {
        const loc = p.location ? `<div style="color:#64748b">${escHtml(p.location)}</div>` : '';
        // Guides get an extra line — total days walked together across
        // all the customer's tours. Connections (above) means tours;
        // days here means the actual days hired. Each day = 1 dot of care.
        // Drivers get the same shape ("X days driven together") but are
        // NOT marked as Pollen Dots of Care — no badge, no halo, no
        // counter contribution. Just a quiet honour to the human at the
        // wheel.
        let daysLine = '';
        if (p.type === 'guide' && p.guide_days) {
          daysLine = `<div style="color:#92400e;font-size:10px;margin-top:1px;display:flex;align-items:center;gap:4px"><span style="display:inline-block;width:6px;height:6px;border-radius:9999px;background:#f59e0b"></span>${p.guide_days} day${p.guide_days === 1 ? '' : 's'} walked together</div>`;
        } else if (p.type === 'driver' && p.driver_days) {
          daysLine = `<div style="color:#475569;font-size:10px;margin-top:1px;display:flex;align-items:center;gap:4px"><span style="display:inline-block;width:6px;height:6px;border-radius:9999px;background:#f59e0b"></span>${p.driver_days} day${p.driver_days === 1 ? '' : 's'} driven together</div>`;
        }
        const careHtml = careBadgeHtml(p.initiatives, 10, !!p.force_care);
        bodyHtml = `<div style="font-size:11px;line-height:1.4">
          <div style="font-weight:600">${escHtml(p.name || '')}</div>
          ${labelLine}
          ${loc}
          ${tripLine}
          ${daysLine}
          ${careHtml}
          ${pinNote}
        </div>`;
      }
      marker.bindPopup(bodyHtml, {
        // Close behaviour:
        //  - clicking another pin closes the current popup (autoClose)
        //  - clicking the map background closes it (closeOnClick)
        //  - the × button still closes it explicitly.
        autoClose: true,
        closeOnClick: true,
        // Hard cap so a rich activity/hotel narrative can never push the
        // popup past the map card. The inner body scrolls vertically when
        // the note is long; Leaflet's autoPan keeps the popup in view.
        maxWidth: 280,
        maxHeight: 260,
        autoPan: true,
        autoPanPaddingTopLeft: [12, 12],
        autoPanPaddingBottomRight: [12, 12],
        className: 'pollen-rich-popup',
      });
      if (onPinClick) marker.on('click', () => onPinClick(p));
      // Stops AND activities bypass the cluster:
      //  - stops are visually distinct teardrop pins.
      //  - activities carry the customer's most meaningful "Pollen Dot of
      //    Care" narrative — clustering hides them, and the cluster
      //    spiderfy animation kept stealing focus from the popup
      //    (popup opened then immediately closed).
      if (isStop || isActivity) {
        marker.addTo(m);
      } else {
        group.addLayer(marker);
      }
      added.push(marker);
    });
    m.addLayer(group);
    clusterRef.current = group;
    markersRef.current = added;

    if (homeBounds) {
      // Fixed opening frame (e.g. India + neighbours) regardless of how far
      // the dots spread — travellers can still pan/zoom to distant dots.
      m.fitBounds(L.latLngBounds(homeBounds), { padding: [20, 20] });
    } else if (valid.length === 1) m.setView([valid[0]._plotLat, valid[0]._plotLng], 10);
    else {
      const b = L.latLngBounds(valid.map(p => [p._plotLat, p._plotLng]));
      // Small journeys (≤ ~20 pins) fit tighter so clusters break apart and
      // individual colour-coded pins are immediately visible. Large atlases
      // keep a looser zoom so clustering can gracefully summarise dense areas.
      m.fitBounds(b, { padding: [36, 36], maxZoom: valid.length <= 20 ? 8 : 6 });
    }

    return () => {
      try { m.removeLayer(group); } catch (e) { /* ignore */ }
      // Stops live outside the cluster; remove them individually too.
      added.forEach(mk => { try { mk.remove(); } catch (e) { /* ignore */ } });
      if (clusterRef.current === group) clusterRef.current = null;
      if (markersRef.current === added) markersRef.current = [];
    };
  }, [pins, L, onPinClick]);

  if (!ready) {
    return (
      <div className={`rounded-lg border bg-muted/20 flex items-center justify-center${aspect ? ` ${aspect}` : ''}`} style={height ? { height } : undefined}>
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  const wrapStyle = height ? { height, width: '100%' } : { width: '100%' };
  const wrapClass = `rounded-lg overflow-hidden border${aspect ? ` ${aspect}` : ''}`;
  return <div ref={containerRef} className={wrapClass} style={wrapStyle} data-testid="pollen-map-wrap" />;
});

PollenDotsMap.displayName = 'PollenDotsMap';
export default PollenDotsMap;
